import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Model} from '../../../app.model';
import {Company, Contact} from '../../../company/company.model';
import {PaymentDetails, SaleOrder} from '../../../buy/buy.model';
import {TariffFlatOffer, Tariff, tariffFlatNames} from '../../../tariff/tariff.model';
import {BuyService} from '../../../buy/buy.service';
import {SubscriptionService} from '../../../subscription/subscription.service';
import {MessageService} from 'primeng/api';
import {I18NextService} from 'angular-i18next';
import {firstValueFrom} from "rxjs";
import {runInAction} from "mobx";
import {CompanyService} from "../../../company/company.service";
import {HttpClient} from "@angular/common/http";
import {WorkflowPartnerService} from '../workflow-partner.service';
import {BaseWorkflowModel, WorkflowPartnerComponent} from '../workflow-partner.component';
import {CustomerService} from "src/app/customer/customer.service";

type WorkflowModel = BaseWorkflowModel & {
    cu?: number,
    tariffList?: Tariff[],
    currentPlan: string,
    selectedFlat?: string,
    tariffFlatOffer: TariffFlatOffer,
    hasSelectableFlatFull: boolean
}

@Component({
    selector: 'app-workflow-partner-one-tariff',
    templateUrl: './workflow-partner-one-tariff.component.html',
    standalone: false
})
export class WorkflowPartnerOneTariffComponent extends WorkflowPartnerComponent<WorkflowModel> implements OnInit, OnDestroy {
  get workflowName(): string {
    return 'change-tariff';
  }

  constructor(protected router: Router,
              protected m: Model,
              protected route: ActivatedRoute,
              protected buyService: BuyService,
              protected subscriptionService: SubscriptionService,
              protected messageService: MessageService,
              protected i18next: I18NextService,
              protected companyService: CompanyService,
              protected PartnerService: WorkflowPartnerService,
              protected customerService: CustomerService,
              protected http: HttpClient) {
    super(route, router, subscriptionService, buyService, m, http, PartnerService, companyService, customerService);
  }

  async ngOnInit() {
    await super.ngOnInit();

    if (!this.checkSubscriptionValid) {
        this.resetWorkflow();
        return;
    }

    if (this.hasExistingTariffOrder) {
        this.resetWorkflow();
        return;
    }

    const result = await firstValueFrom(this.http.get<Tariff[]>('/services/products/flatrates'));

    runInAction(() => {
      this.model.tariffFlatOffer = new TariffFlatOffer();
      this.model.tariffList = [];
      this.model.hasSelectableFlatFull = false;

      for (const flat of result) {
        if (flat.name === 'PC-ONE-TARIFF-FLAT-FULL') this.model.hasSelectableFlatFull = true;
        this.model.tariffList.push(Tariff.fromJson(flat));
      }

      this.model.currentPlan = this.model.subscription.tariffPlanDetail.currentPlan;
    });

    await this.loadPartnerContext();

    if (this.model.hasSelectableFlatFull){
      this.navigate(['choose-tariff-variant']);
    } else {
      this.navigate(['choose-tariff']);
    }

    this.initialized = true;
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  private get hasExistingTariffOrder(): SaleOrder {
    return this.model.subscription.orders.find(order =>
      order.lines.find(line =>
          tariffFlatNames.includes(line.product)
      )
    );
  }

  // Step - Select Tariff Variant
  public onTariffVariantSelected(selectedTariff: string) {
    this.model.tariffFlatOffer.tariff_plan = selectedTariff;

    if (selectedTariff.includes('FLAT-FULL'))
      this.navigate(['delivery-date']);
    else
      this.navigate(['choose-tariff']);
  }

  public onTariffSelected(selectedFairFlat: string) {
    if (selectedFairFlat.toUpperCase() === this.model.currentPlan.toUpperCase()) {
      this.messageService.add({severity: 'error',
        summary: this.i18next.t('chosen_current_same_tariff') as string,
        detail: this.i18next.t('chosen_current_same_tariff_details') as string
      });
      return;
    }
    this.model.tariffFlatOffer.tariff_plan = selectedFairFlat;
    this.navigate(['delivery-date']);
  }

  public onTariffSelectedBack(){
    if (this.model.hasSelectableFlatFull){
      this.navigate(['choose-tariff-variant']);
    } else {
      this.resetWorkflow();
    }
  }


  // Step - Select Delivery Date 
  public onTariffDeliveryDateBack() {
    if (this.model.hasSelectableFlatFull){
      this.navigate(['choose-tariff-variant']);
    } else {
      this.navigate(['choose-tariff']);
    }
  }

  public onTariffDeliveryDateSelected(deliveryDate: string) {
      this.model.tariffFlatOffer.delivery_date = deliveryDate;
      this.navigate(['contact']);
  }


  // Step - Select a Contact
  public onContact(c: Contact) {
    runInAction(() => {
      this.model.contact = c;
    });
    this.navigate(['invoice-address']);
  }
  
  public onContactBack() {
    this.navigate(['delivery-date']);
  }


  // Step - Fill Invoice Address
  public onInvoiceAddressBack() {
    this.navigate(['contact']);
  }

  public onInvoiceAddressFilled(company: Company) {
    this.navigate(['payment']);
  }


  // Step - Select Payment Methode
  public onPaymentMethodBack() {
    this.navigate(['invoice-address']);
  }

  public async onPaymentMethodSelected(payment: PaymentDetails) {
    await this.setPaymentDetails(payment);

    this.model.tariffFlatOffer.partner_id = +this.model.contact.id;

    const SaleOrder = await firstValueFrom(this.PartnerService.createTariffOffer(this.model.tariffFlatOffer, this.model.subscription.appliance_id));
    runInAction(() => {
      this.model.saleOrder = SaleOrder;
    })
    this.navigate(['checkout-summary']);
  }


  // Step - Show Summary
  public onSummaryBack() {
    this.m.buy.resetCurrentOrder();
    this.resetWorkflow();
  }

  public async onSummaryBuy() {
    await firstValueFrom(this.buyService.confirmPartnerQuotation()).then(
      (saleOrder: SaleOrder) => {
        this.navigate(['complete']);
      }
    );
  }
}
